/**
 * How a checkout ended, handed from the page that ran it to the route that
 * reports it.
 *
 * There is no step machine any more, and this is what is left of one. The
 * checkout is a single screen: fill the details, press Pay now, and the
 * gateway's own window takes it from there — every choice that used to be a
 * step of ours (which method, which bank, entering a card, waiting for an OTP)
 * happens inside that window now.
 *
 * What still has to cross a page boundary is the answer. The result lives at
 * its own route so the gateway can be given a `callback_url` for the flows
 * that redirect, and a fresh page load carries no router state — so the
 * browser's account of what happened is left here and picked up there.
 *
 * `sessionStorage` and not `localStorage`: this is one purchase in one tab,
 * and it should not outlive the tab that started it. Nothing sensitive goes in
 * — an outcome and, where there is one, the backend's own id for the
 * subscription. No card ever touches this file, or any storage at all; see
 * `useRazorpayCheckout` for where card details do and do not go.
 */

import type { BillingCycleId } from "./types/billing.types";
import type { PurchaseKind } from "./types/prepaid.types";

/**
 * How a checkout ended.
 *
 * `pending` is not a failure and must not be drawn as one: a mandate can sit
 * with an issuer for hours, and the subscription may well activate long after
 * the person has closed the tab. `unavailable` is this build having no gateway
 * behind it at all — see `SUBSCRIPTIONS_LIVE`.
 */
export type CheckoutOutcome =
  | { state: "active" }
  | { state: "pending"; reason: string }
  | { state: "failed"; reason: string }
  | { state: "unavailable" };

/** What the result route needs to report on: the outcome the browser saw, and
 *  what to confirm and describe it with. */
export interface StashedResult {
  outcome: CheckoutOutcome;
  /** The backend's `public_id` for the subscription, where one was issued. */
  subscriptionId: string | null;
  /**
   * Razorpay's own id for the payment, where one was taken.
   *
   * Shown on the receipt as the reference. It is the number a support
   * conversation about a missing payment actually turns on — the one both
   * sides can look up — which is why it is worth carrying across a page
   * boundary rather than waiting for the backend to report it.
   */
  paymentId?: string | null;
  /**
   * What the subscription was actually bought at, tax and coupon included —
   * off the quote the purchase came back carrying.
   *
   * Carried across rather than worked out again at the other end, because the
   * other end cannot: the result page knows the plan but not which coupon was
   * on the order, and a receipt that quotes the list price for a discounted
   * purchase is worse than one that quotes nothing. What the backend has since
   * recorded still outranks it — see `SubscriptionResultPage`.
   */
  amount?: number | null;
  /**
   * The coupon the order was bought with, and what it took off.
   *
   * Carried for the same reason `amount` is, and it is the stronger case: the
   * result screen knows the plan but has no way at all to learn what coupon was
   * on it — the subscription record does not carry one — so a discounted
   * purchase would otherwise be receipted with no sign that anything came off.
   */
  couponCode?: string | null;
  discount?: number | null;
  /**
   * What the backend made of the purchase — `"UPGRADE"` and the rest.
   *
   * Carried so the receipt can head itself correctly from its first paint. The
   * order it polls says the same thing and says it authoritatively, but that
   * answer is a second or two away, and a page that opens on "Payment
   * successful" and turns into "You're on 12 months" has changed its mind in
   * front of somebody about what they have just bought.
   *
   * Absent on a result stashed by an older build, and on the one path that
   * settles with no order behind it. The poll corrects it either way.
   */
  purchaseKind?: PurchaseKind | null;
  /**
   * The day the term that was bought begins, as the ISO day on the order.
   *
   * Today on every purchase but the queued one, where it is the day after the
   * term already running ends — and on that one it is the whole point of the
   * receipt, which is confirming something that has not started yet. Carried
   * for the same reason `purchaseKind` is: the order says it too, and says it
   * a second or two later.
   */
  startsOn?: string | null;
  /**
   * The payment was taken in test mode with no subscription behind it.
   *
   * Nothing sets this any more: a purchase creates a real subscription now,
   * against whichever key the backend hands over, so a test-mode payment has a
   * test-mode subscription behind it and the receipt has nothing to disown. It
   * is still read, because a tab may be holding a result stashed by an older
   * build of this app.
   */
  testMode?: boolean;
}

const key = (cycle: BillingCycleId) => `naurist.checkout.result.${cycle}`;

const isOutcome = (value: unknown): value is CheckoutOutcome => {
  const outcome = value as CheckoutOutcome | null;
  return (
    !!outcome &&
    typeof outcome === "object" &&
    ["active", "pending", "failed", "unavailable"].includes(outcome.state)
  );
};

const isStashed = (value: unknown): value is StashedResult => {
  const stashed = value as StashedResult | null;
  return (
    !!stashed &&
    typeof stashed === "object" &&
    isOutcome(stashed.outcome) &&
    (stashed.subscriptionId === null ||
      typeof stashed.subscriptionId === "string")
  );
};

/**
 * Every read is defended. Session storage can be full, disabled by policy, or
 * holding something written by an older version of this code — none of which
 * is a reason for the result screen to fail to render. A bad read is simply
 * "no outcome", which sends the reader back to the plans.
 */
export const stashResult = (
  cycle: BillingCycleId,
  outcome: CheckoutOutcome,
  extra: Partial<Omit<StashedResult, "outcome">> = {}
) => {
  try {
    window.sessionStorage.setItem(
      key(cycle),
      // `subscriptionId` is defaulted before the spread rather than after, so
      // a caller that leaves it out gets `null` and one that passes it wins.
      JSON.stringify({ subscriptionId: null, ...extra, outcome } as StashedResult)
    );
  } catch {
    // A result that cannot be remembered still shows, as long as the page is
    // not reloaded. Not worth failing a purchase over.
  }
};

export const readResult = (cycle: BillingCycleId): StashedResult | null => {
  try {
    const raw = window.sessionStorage.getItem(key(cycle));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isStashed(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const clearResult = (cycle: BillingCycleId) => {
  try {
    window.sessionStorage.removeItem(key(cycle));
  } catch {
    /* see `stashResult` */
  }
};
